// Wallet transaction history — /vault/history?currency=TRX_USDT
//
// Date-grouped list of sends and receives for a single wallet currency.
// Back button returns to /vault?currency=TRX_USDT (detail view).

import { useInfiniteQuery } from '@tanstack/react-query';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, type ReactNode } from 'react';

import { getWalletTransactions, type WalletTx, type WalletTxPage } from '../api/client.ts';
import { IconChevronLeft } from '../components/icons.tsx';
import { InfiniteFooter } from '../components/infinite-footer.tsx';
import { ErrorState } from '../components/states.tsx';

import { rootRoute } from './__root.tsx';

export interface WalletHistorySearch {
  readonly currency: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const tokenFromCurrency = (currency: string): string =>
  currency.includes('_') ? (currency.split('_')[1] ?? currency) : currency;

const dateKey = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1)}-${String(d.getDate())}`;
};

const dateLabel = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown';
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const day = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  if (d.toDateString() === today.toDateString()) return `Today · ${day}`;
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday · ${day}`;
  const sameYear = d.getFullYear() === today.getFullYear();
  return d.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
};

const formatTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

// Middle-truncate a tx hash or address.
const truncate = (v: string | undefined, head = 8, tail = 6): string => {
  if (v === undefined || v === '') return '—';
  if (v.length <= head + tail + 1) return v;
  return `${v.slice(0, head)}…${v.slice(-tail)}`;
};

// Pages are 0-based and the service owns the page size, so the next index is
// simply how many pages are already in hand. An empty page ends the feed even
// if total disagrees, so a stale total cannot drive an endless fetch loop.
const nextWalletPage = (
  lastPage: WalletTxPage,
  allPages: readonly WalletTxPage[],
): number | undefined => {
  if (lastPage.txs.length === 0) return undefined;
  const loaded = allPages.reduce((n, page) => n + page.txs.length, 0);
  return loaded < lastPage.total ? allPages.length : undefined;
};

// ─── Transaction row ──────────────────────────────────────────────────────────

const WalletTxRow = ({
  tx,
  token,
}: {
  readonly tx: WalletTx;
  readonly token: string;
}): ReactNode => {
  const isReceive = tx.direction.toUpperCase() === 'RECEIVE';
  const isPending = tx.status.toUpperCase() === 'PENDING';
  const dotClass = isPending ? 'pending' : isReceive ? 'receive' : 'send';
  const amountClass = isPending ? 'pending' : isReceive ? 'credit' : 'debit';
  const sign = isReceive ? '+' : '−';
  const counterparty = isReceive ? truncate(tx.fromAddress) : truncate(tx.toAddress);
  const time = formatTime(tx.createdAt);

  return (
    <div className="tx-row">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flex: 1, minWidth: 0 }}>
        <span className={`wallet-tx-dot ${dotClass}`} aria-hidden="true" />
        <div className="tx-left">
          <p className="tx-desc">
            {isReceive ? 'Received' : 'Sent'}
            {isPending ? (
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 9,
                  fontWeight: 500,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase' as const,
                  background: 'rgba(201,169,97,0.12)',
                  color: 'var(--pending)',
                  padding: '2px 5px',
                  borderRadius: 3,
                  verticalAlign: 'middle',
                }}
              >
                Pending
              </span>
            ) : null}
          </p>
          <p className="tx-meta">
            {isReceive ? `From ${counterparty}` : `To ${counterparty}`}
            {time !== '' ? ` · ${time}` : ''}
          </p>
        </div>
      </div>
      <div className="tx-right">
        <p className={`tx-amount ${amountClass}`}>
          {sign}
          {tx.amount} {token}
        </p>
      </div>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const WalletHistoryPage = (): ReactNode => {
  const navigate = useNavigate();
  const { currency } = walletHistoryRoute.useSearch();
  const token = tokenFromCurrency(currency);

  const txQ = useInfiniteQuery({
    queryKey: ['wallet-txns', currency],
    queryFn: ({ pageParam, signal }): Promise<WalletTxPage> =>
      getWalletTransactions(currency, pageParam, signal),
    initialPageParam: 0,
    getNextPageParam: nextWalletPage,
    staleTime: 30_000,
    // Refetching an infinite query replays every page it holds, so a focus
    // event on a deeply scrolled history is a burst of sequential requests.
    refetchOnWindowFocus: false,
    enabled: currency !== '',
  });

  // A refetched page can repeat a row that a newer send has pushed across the
  // page boundary, so de-duplicate on id before grouping.
  const txs = useMemo<readonly WalletTx[]>(() => {
    const seen = new Set<string>();
    const flat: WalletTx[] = [];
    for (const page of txQ.data?.pages ?? []) {
      for (const tx of page.txs) {
        if (seen.has(tx.id)) continue;
        seen.add(tx.id);
        flat.push(tx);
      }
    }
    return flat;
  }, [txQ.data?.pages]);

  // Date-group the transactions, preserving server sort order (newest first).
  const groups = useMemo(() => {
    const buckets = new Map<string, { iso: string; txs: WalletTx[] }>();
    const order: string[] = [];
    for (const tx of txs) {
      const key = dateKey(tx.createdAt);
      const existing = buckets.get(key);
      if (existing === undefined) {
        buckets.set(key, { iso: tx.createdAt, txs: [tx] });
        order.push(key);
      } else {
        existing.txs.push(tx);
      }
    }
    return order.map((k) => {
      const b = buckets.get(k) as { iso: string; txs: WalletTx[] };
      return { key: k, iso: b.iso, txs: b.txs };
    });
  }, [txs]);

  const goBack = (): void => {
    void navigate({ to: '/vault', search: { currency } });
  };

  if (txQ.isError && txs.length === 0) {
    return (
      <ErrorState
        message="Could not load history"
        onRetry={(): void => {
          void txQ.refetch();
        }}
      />
    );
  }

  return (
    <main className="screen">
      <div className="topbar fade-in">
        <button type="button" className="topbar-back" aria-label="Back" onClick={goBack}>
          <IconChevronLeft />
        </button>
        <p className="eyebrow">{token} History</p>
        <span className="topbar-spacer" aria-hidden="true" />
      </div>

      {txQ.isLoading ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <div className="loading-spinner" style={{ margin: '0 auto' }} aria-hidden="true" />
        </div>
      ) : groups.length === 0 ? (
        <div className="empty-state">No transactions yet</div>
      ) : (
        <div className="fade-in d1">
          {groups.map((g) => (
            <section key={g.key}>
              <div className="tx-date-header">
                <p className="tx-date-label">{dateLabel(g.iso)}</p>
              </div>
              {g.txs.map((tx, i, arr) => (
                <div key={tx.id}>
                  <WalletTxRow tx={tx} token={token} />
                  {i < arr.length - 1 ? <div className="hairline" /> : null}
                </div>
              ))}
            </section>
          ))}

          <InfiniteFooter
            hasMore={txQ.hasNextPage}
            isLoading={txQ.isFetchingNextPage}
            isError={txQ.isFetchNextPageError}
            onLoadMore={(): void => {
              void txQ.fetchNextPage();
            }}
            onRetry={(): void => {
              void txQ.fetchNextPage();
            }}
            endLabel="No older transactions"
          />
        </div>
      )}
    </main>
  );
};

export const walletHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vault/history',
  component: WalletHistoryPage,
  validateSearch: (s: Record<string, unknown>): WalletHistorySearch => ({
    currency: typeof s.currency === 'string' ? s.currency : '',
  }),
});
